import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchOrgScannedBill, reviewOrgScannedBill } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../context/CurrencyProvider';

const OrganizationScannedBillDetail = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [scan, setScan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('');
    const [notes, setNotes] = useState('');
    const statusOptions = [
        { value: 'org_review', label: 'org_review' },
        { value: 'processed', label: 'processed' },
        { value: 'rejected', label: 'rejected' },
    ];

    const loadScan = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchOrgScannedBill(id);
            setScan(data);
            setStatus(data.status || '');
            setNotes(data.org_review_notes || '');
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadScan();
    }, [id]);

    const handleReview = async () => {
        await reviewOrgScannedBill(id, {
            status,
            org_review_notes: notes,
        });
        loadScan();
    };

    if (loading) {
        return <LoadingMessage>{t('ScannedBills.loading')}</LoadingMessage>;
    }

    if (error || !scan) {
        return <ErrorMessage>{t('ScannedBills.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('ScannedBills.detailTitle')}</PageTitle>
            </PageHeader>
            <PageContent>
                <StyledCard>
                    <div style={{ display: 'grid', gap: 12 }}>
                        <div><strong>{t('ScannedBills.columns.status')}:</strong> {scan.status}</div>
                        <div><strong>{t('ScannedBills.columns.total')}:</strong> {scan.total_amount != null ? formatResolvedAmount({
                            eurAmount: scan.total_amount,
                            localAmount: scan.total_amount_local,
                            localCurrencyId: scan.currency_id,
                        }) : '-'}</div>
                        <div><strong>{t('ScannedBills.columns.user')}:</strong> {scan.user?.email ?? '-'}</div>
                    </div>
                </StyledCard>

                <StyledCard>
                    <h3>{t('ScannedBills.review')}</h3>
                    <div style={{ display: 'grid', gap: 12 }}>
                        <SimpleSelect
                            value={status}
                            onChange={(event, nextValue) => setStatus(nextValue ?? event?.target?.value ?? '')}
                            options={statusOptions}
                            inputId="scan-review-status"
                        />
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={t('ScannedBills.reviewNotes')}
                        />
                        <button type="button" className="btn" onClick={handleReview}>
                            {t('ScannedBills.saveReview')}
                        </button>
                    </div>
                </StyledCard>

                <StyledCard>
                    <h3>{t('ScannedBills.previewImages')}</h3>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {(scan.images || []).map(image => (
                            <img
                                key={image.id}
                                src={image.image_url}
                                alt="scan"
                                style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 12 }}
                            />
                        ))}
                    </div>
                </StyledCard>
            </PageContent>
        </PageContainer>
    );
};

export default OrganizationScannedBillDetail;
